import styled from "styled-components";

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Por si la parte fija de la ficha no cabe en una ventana muy baja. */
  overflow-y: auto;
  padding: 24px 16px;
`;

// Alto máximo: si el contenido no cabe, solo scrollea el TabPanel; cabecera,
// imagen, pestañas y pie quedan fijos (flex-shrink: 0 en todo lo demás).
export const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 560px;
  max-height: min(860px, calc(100vh - 48px));
  box-sizing: border-box;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);

  & > * { flex-shrink: 0; }
`;

/* ── Cabecera ── */
export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// El PNG ya trae su propio fondo, asi que no lleva envoltorio de color.
export const TypeIcon = styled.img`
  width: 42px;
  height: 42px;
  flex-shrink: 0;
`;

export const Title = styled.h2`
  flex: 1;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 500;
  color: #1b2b3a;
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: none;
  background: none;
  border-radius: 6px;
  color: #8fa3b0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: #f0f3f5; color: #1b2b3a; }
`;

/* ── Prescripcion (solo terapeuta) ── */
export const PrescribeRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.88rem;
  color: #1b2b3a;
  cursor: pointer;
  width: fit-content;
`;

export const PrescribeBox = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $checked }) => ($checked ? "#1b6b78" : "#fff")};
  border: 1.5px solid ${({ $checked }) => ($checked ? "#1b6b78" : "#c9d2d9")};
`;

// El checkbox real queda oculto pero accesible: es el que recibe el foco y el
// que anuncia el lector de pantalla; el PrescribeBox solo lo dibuja.
export const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  margin: 0;

  &:focus-visible + ${PrescribeBox} { outline: 2px solid #26A9B6; outline-offset: 2px; }
`;

/* ── Imagen ── */
export const Image = styled.img`
  width: 100%;
  aspect-ratio: 2 / 1;
  object-fit: cover;
  border-radius: 8px;
  background: #f0f3f5;
`;

/* ── Objetivo ── */
export const SectionLabel = styled.h3`
  margin: 4px 0 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1b2b3a;
`;

export const Text = styled.p`
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${({ $muted }) => ($muted ? "#8a959e" : "#333")};
  /* Los textos de la BD traen saltos \r\n entre parrafos. */
  white-space: pre-line;
`;

/* ── Pestanas ── */
export const TabsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 4px;
  border-radius: 20px;
  background: #f0f3f5;
  margin-top: 4px;
`;

export const Tab = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 12px;
  border: none;
  border-radius: 16px;
  font-size: 0.82rem;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
  background: ${({ $active }) => ($active ? "#1b6b78" : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#6b7a85")};

  &:hover { color: ${({ $active }) => ($active ? "#fff" : "#1b2b3a")}; }
  &:disabled { cursor: default; }
  &:disabled:hover { color: #6b7a85; }
`;

// La unica parte que encoge y scrollea. && para ganar al '& > *' del Modal.
// El min-height evita que en una ventana muy baja quede aplastado del todo.
export const TabPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;

  && {
    flex: 1 1 auto;
    min-height: 120px;
    overflow-y: auto;
    overscroll-behavior: contain;
    /* Hueco para que la barra no pegue al texto ni tape el boton de quitar. */
    padding-right: 6px;
  }

  &:focus-visible { outline: 2px solid #26A9B6; outline-offset: 2px; border-radius: 6px; }
`;

/* ── Edad sugerida y clinical notes (solo terapeuta) ── */
export const MetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const MetaLine = styled.p`
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #8a959e;
  white-space: pre-line;

  strong { font-weight: 600; color: #1b2b3a; }
`;

/* ── Context / Materials / Postures ── */
export const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RemoveButton = styled.button`
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  border-radius: 6px;
  background: none;
  color: #b3264f;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover { background: #fbeef2; }
`;

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 11px;
  border: none;
  border-radius: 6px;
  background: #e8f0f2;
  color: #1b6b78;
  font-size: 0.85rem;
  font-family: inherit;
  cursor: pointer;

  &:hover { background: #dbe8eb; }
`;

/* ── Pie (solo terapeuta) ── */
export const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

export const FooterRight = styled.div`
  display: flex;
  gap: 8px;
`;

export const OutlineButton = styled.button`
  padding: 7px 13px;
  border-radius: 6px;
  border: 1px solid #1b6b78;
  background: #fff;
  color: #1b6b78;
  font-size: 0.82rem;
  font-family: inherit;
  cursor: pointer;

  &:hover { background: #f0f7f8; }
  &:disabled { opacity: 0.45; cursor: default; }
  &:disabled:hover { background: #fff; }
`;

export const PrimaryButton = styled.button`
  padding: 7px 13px;
  border-radius: 6px;
  border: 1px solid #1b6b78;
  background: #1b6b78;
  color: #fff;
  font-size: 0.82rem;
  font-family: inherit;
  cursor: pointer;

  &:hover { background: #175a65; }
  &:disabled { opacity: 0.45; cursor: default; }
  &:disabled:hover { background: #1b6b78; }
`;

export const SaveError = styled.p`
  margin: 8px 0 0;
  font-size: 0.8rem;
  color: #b3264f;
  text-align: right;
`;

export const Status = styled.p`
  margin: 0;
  padding: 24px 0;
  text-align: center;
  font-size: 0.85rem;
  color: #999;
`;
